import {
  Button,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
} from '@fluentui/react-components';
import {
  Delete20Regular,
  FolderArrowRight20Regular,
  Link20Regular,
  LockClosed20Regular,
  MoreHorizontal20Regular,
  Rename20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';

export type FolderAction = 'rename' | 'move' | 'permissions' | 'share' | 'delete';

export interface FolderMenuProps {
  /** Names the folder for screen readers; omitted for the open folder's own menu. */
  folderName?: string;
  /** Offer "Permissions" (it reads the organisation's members; not available to partners). */
  withPermissions?: boolean;
  /** Offer "Share by link" (the caller manages the space; GEDX-FE-03). */
  withShare?: boolean;
  onAction: (action: FolderAction) => void;
}

/** "More actions" menu of a folder the caller manages: rename, move, permissions, delete. */
export function FolderMenu({
  folderName,
  withPermissions = true,
  withShare = false,
  onAction,
}: FolderMenuProps) {
  const { t } = useTranslation('documents');
  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          appearance="subtle"
          icon={<MoreHorizontal20Regular />}
          aria-label={folderName ? t('actions.moreFor', { name: folderName }) : t('actions.more')}
        />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem
            icon={<Rename20Regular />}
            onClick={() => {
              onAction('rename');
            }}
          >
            {t('actions.rename')}
          </MenuItem>
          <MenuItem
            icon={<FolderArrowRight20Regular />}
            onClick={() => {
              onAction('move');
            }}
          >
            {t('actions.move')}
          </MenuItem>
          {withPermissions ? (
            <MenuItem
              icon={<LockClosed20Regular />}
              onClick={() => {
                onAction('permissions');
              }}
            >
              {t('actions.permissions')}
            </MenuItem>
          ) : null}
          {withShare ? (
            <MenuItem
              icon={<Link20Regular />}
              onClick={() => {
                onAction('share');
              }}
            >
              {t('shareLinks:action.open')}
            </MenuItem>
          ) : null}
          <MenuItem
            icon={<Delete20Regular />}
            onClick={() => {
              onAction('delete');
            }}
          >
            {t('actions.delete')}
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
